import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowSquareOut, PencilSimple, UsersThree, Receipt } from "@phosphor-icons/react";
import { FeedbackInsights, avgOf, Stars } from "./FeedbackInsights";
import { toast } from "sonner";
import { api, errMsg, img, guestUrl, fmtDate, downloadReceipt } from "@/lib/api";
import { blankProperty } from "@/lib/data";
import { useAuth } from "@/lib/auth";
import { CopyButton } from "@/components/common";
import { Skeleton } from "@/components/ui/skeleton";

export const PageHead = ({ eyebrow, title, children }) => (
  <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
    <div><p className="label-xs text-[#D96C4A]">{eyebrow}</p><h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">{title}</h1></div>
    {children}
  </div>
);

export default function Properties() {
  const [items, setItems] = useState(null);
  const [recent, setRecent] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { user, refresh } = useAuth();

  useEffect(() => {
    api.get("/properties").then((r) => setItems(r.data));
    api.get("/checkins").then((r) => setRecent(r.data.slice(0, 6))).catch(() => {});
    api.get("/feedback").then((r) => setFeedback(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      const data = blankProperty();
      data.host.name = user?.name || "";
      data.form.email = user?.email || "";
      const r = await api.post("/properties", { data });
      refresh();
      nav(`/dashboard/properties/${r.data.property_id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const propName = (id) => items?.find((p) => p.property_id === id)?.data.name || "";

  return (
    <div data-testid="properties-page">
      <PageHead eyebrow="Dashboard" title={`Karibu, ${user?.name?.split(" ")[0] || "host"}`}>
        <div className="flex flex-wrap gap-2">
        <Link to="/dashboard/documents?new=invoice" data-testid="dashboard-new-invoice-button" className="inline-flex h-11 items-center gap-2 rounded-lg bg-[#0E1729] px-4 font-semibold text-white"><Receipt /> Invoice / receipt</Link>
        <button onClick={create} disabled={busy} data-testid="add-property-button" className="inline-flex h-11 items-center gap-2 rounded-lg bg-[#D96C4A] px-5 font-semibold text-white transition-colors duration-200 hover:bg-[#C25D3E] disabled:opacity-60"><Plus weight="bold" /> Add an Airbnb</button>
        </div>
      </PageHead>

      {!items ? (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">{[1, 2].map((i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border bg-white p-12 text-center" data-testid="properties-empty">
          <h2 className="font-display text-2xl">Set up your first guest page</h2>
          <p className="mx-auto mt-2 max-w-md text-muted-foreground">Add your Airbnb, fill in Wi-Fi, directions and rules, and you'll get a link and QR code to share right away.</p>
          <button onClick={create} disabled={busy} data-testid="empty-add-property-button" className="mt-6 inline-flex h-11 items-center gap-2 rounded-lg bg-[#151312] px-5 font-semibold text-white"><Plus weight="bold" /> Add an Airbnb</button>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => (
            <article key={p.property_id} className="group overflow-hidden rounded-2xl border border-border bg-white" data-testid={`property-card-${p.property_id}`}>
              <div className="relative h-40 bg-secondary">
                {p.data.cover && <img src={img(p.data.cover)} alt="" className="h-full w-full object-cover" />}
                <span className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold backdrop-blur">{p.data.theme}</span>
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl">{p.data.name}</h3>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><UsersThree size={16} /> {p.checkins} check-in{p.checkins === 1 ? "" : "s"}</p>
                {(() => { const fl = feedback.filter((c) => c.property_id === p.property_id); return fl.length > 0 && <p className="mt-1 flex items-center gap-1.5 text-sm" data-testid={`property-rating-${p.property_id}`}><Stars value={avgOf(fl)} size={14} /> <b>{avgOf(fl).toFixed(1)}</b> <span className="text-muted-foreground">({fl.length})</span></p>; })()}
                <div className="mt-3 flex items-center justify-between rounded-lg bg-secondary px-3 py-2 text-xs">
                  <span className="truncate font-mono">/p/{p.slug}</span>
                  <CopyButton value={guestUrl(p.slug)} testId={`copy-link-${p.property_id}`} className="font-semibold text-[#D96C4A]" done="Link copied" />
                </div>
                <div className="mt-4 flex gap-2">
                  <Link to={`/dashboard/properties/${p.property_id}`} data-testid={`edit-property-${p.property_id}`} className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-[#151312] text-sm font-semibold text-white"><PencilSimple /> Edit</Link>
                  <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`open-property-${p.property_id}`} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-border px-4 text-sm font-semibold hover:border-[#151312] transition-colors duration-200"><ArrowSquareOut /> Open</a>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {recent.length > 0 && (
        <section className="mt-12" data-testid="recent-checkins">
          <h2 className="font-display text-xl">Recent check-ins</h2>
          <div className="mt-4 divide-y divide-border rounded-2xl border border-border bg-white">
            {recent.map((c) => (
              <div key={c.checkin_id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm">
                <span className="font-medium">{c.form.name}</span>
                <span className="text-muted-foreground">{propName(c.property_id)} · {c.nights} night{c.nights === 1 ? "" : "s"}</span>
                <span className="font-mono text-xs">{c.ref}</span>
                <span className="text-muted-foreground">{fmtDate(c.created_at)}</span>
                <button onClick={() => downloadReceipt(c.checkin_id, c.ref)} data-testid={`receipt-button-${c.checkin_id}`} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-semibold transition-colors duration-200 hover:border-[#D96C4A] hover:text-[#D96C4A]"><Receipt /> Receipt</button>
              </div>
            ))}
          </div>
        </section>
      )}

      <FeedbackInsights properties={items} feedback={feedback} />
    </div>
  );
}
